import {apiLink} from "../../app/routes/api_link";
import {IServices} from "../../app/services/IServices";
import {appendQueryString} from "../../app/utils/append_query_string";
import {getRequest} from "../../app/utils/request_response_utils/request";
import {getMakeSlug} from "../../offer/utils/get_make_slug";
import {makeSlugToApiType} from "../../offer/utils/make_slug_to_api_type";
import {ICarsMakesSearch, normalizeMakes} from "./fetch_makes_data_for_search";

type MakesFilterParams = Record<string, string>;

interface ISearchCountResponse {
    count?: number;
    makes?: ICarsMakesSearch[];
}

/**
 * Make counts for current filters (esp. location).
 * Uses get-cars-search `makes` when present; otherwise detects global cars-make-search
 * totals and recounts per make (prod API still ignores location on cars-make-search).
 */
export const fetchLocationScopedMakes = async (
    services: Partial<IServices>,
    knownMakes: ICarsMakesSearch[],
    filters: MakesFilterParams
): Promise<ICarsMakesSearch[]> => {
    const locationParam = filters.location;
    if (!locationParam || !knownMakes.length) {
        return knownMakes;
    }

    const searchUrl = appendQueryString(apiLink.carDynamicSearch({})({}), {...filters, ss: "aB2a"});
    const searchJson = (await getRequest(services, searchUrl, "fetchMakesScopedTotal")) as ISearchCountResponse;

    if (Array.isArray(searchJson?.makes) && searchJson.makes.length) {
        return normalizeMakes(searchJson.makes).filter((m) => (Number(m.count) || 0) > 0);
    }

    const expected = Number(searchJson?.count) || 0;
    const sum = knownMakes.reduce((s, m) => s + (Number(m.count) || 0), 0);
    // Location-aware make list: totals should be close to offer count for that location.
    if (expected > 0 && sum > 0 && sum <= expected * 1.35) {
        return knownMakes.filter((m) => (Number(m.count) || 0) > 0);
    }

    const recounted = await Promise.all(
        knownMakes.map(async (m) => {
            const apiType = makeSlugToApiType(getMakeSlug(m.make) || m.make) || m.make;
            const url = appendQueryString(apiLink.carDynamicSearch({})({}), {
                ...filters,
                make: apiType,
                ss: "aB2a"
            });
            try {
                const json = (await getRequest(services, url, `fetchMakeCount_${apiType}`)) as ISearchCountResponse;
                return {make: m.make, count: Number(json?.count) || 0};
            } catch {
                return {make: m.make, count: 0};
            }
        })
    );

    return recounted.filter((m) => m.count > 0).sort((a, b) => a.make.localeCompare(b.make, "pl"));
};
